/**
 * The claim list.
 *
 * Managers watch this for "where is my reimbursement?" — which is why `paid_at` and the
 * reference are columns rather than something you have to open a record to find (D-29).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { ClaimStatusBadge, Money, PageHeader, RefLink } from '@/components/bits';
import { Badge, Card, EmptyState, Select, Table, Td, Th } from '@/components/ui';
import { activityLabel } from '@/lib/policy';
import { formatDate } from '@/lib/dates';
import type { ClaimStatus } from '@/lib/types';

const GROUPS: Array<{ value: string; label: string; statuses?: ClaimStatus[] }> = [
  { value: 'all', label: 'All' },
  {
    value: 'open',
    label: 'In progress',
    statuses: [
      'draft',
      'sponsor_review',
      'hr_verify',
      'hr_director',
      'clarification_requested',
      'returned',
    ],
  },
  { value: 'approved', label: 'Awaiting payment', statuses: ['approved'] },
  { value: 'paid', label: 'Paid', statuses: ['paid'] },
  { value: 'rejected', label: 'Rejected', statuses: ['rejected'] },
];

export function Claims() {
  const session = useSession();
  const [group, setGroup] = useState('all');
  const chosen = GROUPS.find((entry) => entry.value === group);

  const claims = useQuery({
    queryKey: ['claims', session.year, group],
    queryFn: () =>
      api.claims({
        budget_year: session.year,
        ...(chosen?.statuses ? { status: chosen.statuses } : {}),
        limit: 200,
      }),
  });

  const items = claims.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Claims"
        subtitle={`${session.year} · ${claims.data?.total ?? 0} in total`}
      />

      <Card
        title="Claims"
        action={
          <Select
            aria-label="Filter by stage"
            className="h-8 w-44 py-1"
            value={group}
            onChange={(event) => setGroup(event.target.value)}
          >
            {GROUPS.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </Select>
        }
      >
        {claims.isPending ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <EmptyState title="No claims yet">
            A claim is raised against a confirmed proposal after the event, within 14 days (R-24).
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Event</Th>
                <Th>Department</Th>
                <Th>Date</Th>
                <Th numeric>Cost</Th>
                <Th numeric>Reimbursable</Th>
                <Th numeric>Excess</Th>
                <Th>Paid</Th>
                <Th>Stage</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <Td>
                    <RefLink to={`/claims/${item.id}`} refNo={item.ref_no} />
                    {item.proposal_ref_no ? (
                      <p className="text-muted-foreground text-xs">{item.proposal_ref_no}</p>
                    ) : null}
                  </Td>
                  <Td>{activityLabel(item.activity_type)}</Td>
                  <Td className="text-xs">{item.department_name}</Td>
                  <Td className="whitespace-nowrap">{formatDate(item.actual_date)}</Td>
                  <Td numeric>
                    <Money cents={item.total_cost_cents} />
                  </Td>
                  <Td numeric>
                    <Money cents={item.reimbursable_cents} />
                  </Td>
                  <Td numeric>
                    <Money cents={item.excess_cents} muted />
                  </Td>
                  <Td className="whitespace-nowrap">
                    {item.paid_at ? (
                      formatDate(item.paid_at)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex flex-col items-start gap-1">
                      <ClaimStatusBadge status={item.status} />
                      {item.pending_exceptions > 0 ? (
                        <Badge tone="warn">{item.pending_exceptions} to decide</Badge>
                      ) : null}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
